import type { AppRouter } from '@alison/api/router';
import { createTRPCClient, httpBatchStreamLink, httpLink, splitLink } from '@trpc/client';
import { env } from '../env.js';
import { mintJwtCookie } from './cookies.js';

const url = `${env.apiUrl}/trpc`;

// tRPC's link `fetch` option uses an internal (unexported) `FetchEsque` type that
// is narrower than the global fetch overloads. Derive it from the link options so
// we can supply a credentialed fetch without naming the internal type.
type LinkFetch = NonNullable<NonNullable<Parameters<typeof httpLink>[0]>['fetch']>;

const credentialedFetch = (input: unknown, init: unknown): Promise<Response> =>
  fetch(input as RequestInfo | URL, { ...(init as RequestInit), credentials: 'include' });

// A 401 on a data query means the session token is stale; re-mint once and if
// that fails the session is dead, so redirect to SSO.
export const dataFetch: LinkFetch = async (input, init) => {
  const response = await credentialedFetch(input, init);
  if (response.status !== 401) return response;
  if (!(await mintJwtCookie())) {
    window.location.assign('/login?reason=session-expired');
    return response;
  }
  return credentialedFetch(input, init);
};

// Pre-login ops return the 401 to the caller instead of redirecting, so the
// sign-in page can show an inline error rather than bouncing to SSO mid-action.
export const authFetch: LinkFetch = async (input, init) => {
  const response = await credentialedFetch(input, init);
  if (response.status !== 401) return response;
  if (!(await mintJwtCookie())) return response;
  return credentialedFetch(input, init);
};

export const telemetryFetch: LinkFetch = (input, init) => credentialedFetch(input, init);

const keepaliveFetch: LinkFetch = (input, init) =>
  fetch(input as RequestInfo | URL, {
    ...(init as RequestInit),
    credentials: 'include',
    keepalive: true,
  });

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => op.context['keepalive'] === true,
      true: httpLink({ url, fetch: keepaliveFetch }),
      false: splitLink({
        condition: (op) => op.context['telemetry'] === true,
        true: httpLink({ url, fetch: telemetryFetch }),
        // Set `context: { stream: false }` on a call to opt out of batching/streaming.
        false: splitLink({
          condition: (op) => op.context['stream'] === false,
          true: httpLink({ url, fetch: authFetch }),
          false: httpBatchStreamLink({ url, fetch: dataFetch }),
        }),
      }),
    }),
  ],
});
